/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { RoutingService } from 'src/app/service/routing.service';
import { ConfigService } from 'src/app/service/config.service';
import { EntityInfoService } from 'src/app/shared/services/metadata-api/entity-info.service';
import { yukka, dev } from '../news-board/hierarchy';
import {
  Category,
  Constituent,
  HeatmapCell,
  HeatmapEntity,
  HeatmapMatrix,
  HeatmapMonth,
  HeatmapRegion,
  HeatmapRow,
  HeatmapScoreType,
  HierarchyId,
  TierColor,
  TopEvent,
} from './market-board.types';
import {
  buildHeatmapFromMonthly,
  cellAriaLabel,
  deltaArrow,
  heatmapCellColor,
  idFromCompoundKey,
  lastNMonths,
  monthlyByKeyFromMaps,
  monthRange,
  pickDelta,
  pickScore,
  pickVolume,
  sentimentBucket,
  treemapScores,
  treemapStructure,
} from './market-board.utils';
import {
  CountryBoardDataService,
  DEFAULT_TIMEFRAME,
  PanelTimeframe,
} from './news-market-countries/country-board-data.service';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-news-market-board',
  templateUrl: './news-market-board.component.html',
  styleUrls: ['./news-market-board.component.scss'],
  // Shared by the map (in the countries child) and the heatmap detail panel so
  // hover-prefetch and click reuse one cache.
  providers: [CountryBoardDataService],
})
export class NewsMarketBoardComponent implements OnDestroy, AfterViewInit {
  hierarchies: { label: string; type: string; id: HierarchyId }[];
  selectedTab: HierarchyId = 'country';
  // Country tab only: 'heatmap' (12-month matrix, default) or 'map' (world map).
  view: 'heatmap' | 'map' = 'heatmap';
  window: '24H' | '1W' | '1M' | '3M' | '6M' | '1Y' = '1M';
  sort: 'worst' | 'best' | 'divided' = 'worst';
  categories: Category[] = [];
  loading = false;
  searchTerm = '';

  // Industry sentiment heatmap (industry tab). `activeScore` / `activeRegion`
  // are kept as state so their (currently hidden) selectors can be reintroduced
  // later — the fetch + colouring are already parameterised by them.
  heatmap: HeatmapMatrix | null = null;
  heatmapLoading = false;
  activeScore: HeatmapScoreType = 'sentiment';
  activeRegion: HeatmapRegion = 'global';
  // Sentiment averaging window (days) for the heatmap score_ts request.
  private readonly heatmapPeriod = 30;
  // The 12-month window the current heatmap is built against; reused when
  // lazily fetching a category's sub-category rows on expand.
  private heatmapMonths: HeatmapMonth[] = [];
  private heatmapReference = new Date();
  // Sub-category (child) entities per parent compound key, from the hierarchy.
  private childEntitiesByParent = new Map<string, HeatmapEntity[]>();
  private expandedRows = new Set<string>();
  private subRowsByParent = new Map<string, HeatmapRow[]>();

  // Detail panel opened from a heatmap cell. The entity + timeframe reflect the
  // clicked cell (its entity and calendar month); `panelAggregate` marks the
  // average summary row, whose constituents are the tab's entities themselves.
  panelEntity: Category | null = null;
  panelTimeframe: PanelTimeframe = DEFAULT_TIMEFRAME;
  panelTimeframeLabel = '';
  panelSentimentParams: Record<string, any> = {};
  panelAggregate = false;

  @HostBinding('style.height.px') hostHeight: number | null = null;
  @HostBinding('style.display') hostDisplay = 'block';
  @HostBinding('style.overflow') hostOverflow = 'hidden';
  private resizeObserver?: ResizeObserver;

  private destroy$ = new Subject<void>();

  readonly windows: ('24H' | '1W' | '1M' | '3M' | '6M' | '1Y')[] = [
    '24H',
    '1W',
    '1M',
    '3M',
    '6M',
    '1Y',
  ];

  readonly sortOptions: { key: 'worst' | 'best' | 'divided'; label: string }[] =
    [
      { key: 'worst', label: 'Worst first' },
      { key: 'best', label: 'Best first' },
      { key: 'divided', label: 'Most divided' },
    ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public routing: RoutingService,
    public config: ConfigService,
    private entityInfo: EntityInfoService,
    private hostRef: ElementRef<HTMLElement>,
  ) {
    // Country first in the tab order (the rest keep their configured order).
    const source = (config.appConfig.environment === 'dev' ? dev : yukka) as {
      label: string;
      type: string;
      id: HierarchyId;
    }[];
    this.hierarchies = [
      ...source.filter((h) => h.id === 'country'),
      ...source.filter((h) => h.id !== 'country'),
    ];

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((qp) => {
      const tab = ((qp['tab'] as HierarchyId) ??
        this.hierarchies[0]?.id ??
        'country') as HierarchyId;
      const win = qp['window'] ?? '1M';
      const sort = qp['sort'] ?? 'worst';
      const view = (qp['view'] ?? 'heatmap') as 'heatmap' | 'map';
      const tabChanged = tab !== this.selectedTab;
      const winChanged = win !== this.window;
      const viewChanged = view !== this.view;
      this.selectedTab = tab;
      this.window = win;
      this.sort = sort;
      this.view = view;
      if (tabChanged || winChanged || this.categories.length === 0) {
        this.loadCategories();
      } else if (
        viewChanged &&
        this.selectedTab === 'country' &&
        this.view === 'heatmap' &&
        !this.heatmap
      ) {
        this.loadHeatmap();
      } else {
        this.applySort();
      }
    });
  }

  ngAfterViewInit(): void {
    this.setupHostSizing();
  }

  private setupHostSizing(): void {
    const host = this.hostRef.nativeElement;
    const target = this.findSizingParent(host);
    if (!target) return;
    const update = () => {
      const h = target.getBoundingClientRect().height;
      if (h > 0) this.hostHeight = h;
    };
    update();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => update());
      this.resizeObserver.observe(target);
    }
    window.addEventListener('resize', update);
    this.destroy$.subscribe(() => {
      window.removeEventListener('resize', update);
    });
  }

  private findSizingParent(el: HTMLElement): HTMLElement | null {
    let node: HTMLElement | null = el.parentElement;
    while (node && node !== document.body) {
      const cs = getComputedStyle(node);
      const rect = node.getBoundingClientRect();
      const isBlockish =
        cs.display === 'block' ||
        cs.display === 'flex' ||
        cs.display === 'grid' ||
        cs.display === 'inline-block';
      if (isBlockish && rect.height > 100) return node;
      node = node.parentElement;
    }
    return null;
  }

  selectTab(id: HierarchyId): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: id },
      queryParamsHandling: 'merge',
    });
  }

  selectWindow(win: '24H' | '1W' | '1M' | '3M' | '6M' | '1Y'): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { window: win },
      queryParamsHandling: 'merge',
    });
  }

  selectView(view: 'heatmap' | 'map'): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view },
      queryParamsHandling: 'merge',
    });
  }

  // Country tab renders the world map only in 'map' view; every other case
  // (industry/index, or country in 'heatmap' view) renders the heatmap matrix.
  get showMap(): boolean {
    return this.selectedTab === 'country' && this.view === 'map';
  }

  get showHeatmap(): boolean {
    return !this.showMap;
  }

  selectSort(sort: 'worst' | 'best' | 'divided'): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sort },
      queryParamsHandling: 'merge',
    });
  }

  toggleCategory(cat: Category): void {
    cat.expanded = !cat.expanded;
    if (cat.expanded && cat.constituents.length === 0 && !cat.loading) {
      this.loadConstituents(cat);
    }
  }

  // Marker/row click in the overview scatter → expand that category's row and
  // bring it into view.
  onOverviewSelect(cat: Category): void {
    if (!cat.expanded) this.toggleCategory(cat);
    setTimeout(() => {
      document
        .getElementById('mb-row-' + cat.id)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  trackById(_i: number, cat: Category): string {
    return cat.id;
  }

  trackByKey(_i: number, c: Constituent): string {
    return c.compoundKey;
  }

  get visibleCategories(): Category[] {
    const q = this.searchTerm.trim().toLowerCase();
    if (!q) return this.categories;
    return this.categories.filter((c) => c.name.toLowerCase().includes(q));
  }

  get sentimentParams(): Record<string, any> {
    const windowMap: Record<string, string> = {
      '24H': '1',
      '1W': '7',
      '1M': '30',
      '3M': '90',
      '6M': '180',
      '1Y': '365',
    };
    const days = windowMap[this.window];
    const qp = this.route.snapshot.queryParams;
    return {
      ...qp,
      time: days,
      period: Number(days),
    };
  }

  onSearchChange(value: string): void {
    this.searchTerm = value;
  }

  clearSearch(): void {
    this.searchTerm = '';
  }

  tabLabel(id: HierarchyId): string {
    switch (id) {
      case 'country':
        return 'Countries';
      case 'industry':
        return 'Industries';
      case 'index':
        return 'Indices';
      default:
        return id;
    }
  }

  averageClass(avg: number): string {
    return sentimentBucket(avg);
  }

  impactWidth(impact: number): number {
    return Math.min(50, Math.abs(impact) * 2.5);
  }

  formatVolume(v: number): string {
    if (!v) return '0';
    if (v >= 1000) return (v / 1000).toFixed(1) + 'k';
    return String(v);
  }

  private loadCategories(): void {
    this.loading = true;
    this.categories = [];
    const rootId = this.selectedTab;
    const iparams = this.sentimentParams;

    this.entityInfo
      .getHierarchyInfo(rootId, iparams)
      .pipe(
        catchError((err) => {
          console.error('[market-board] hierarchy fetch failed', err);
          return of({ treemap: { children: [] } });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((res: any) => {
        const children = (res?.treemap?.children ?? []) as any[];
        if (children.length === 0) {
          this.loading = false;
          return;
        }
        this.categories = children.map((node) => this.mapNodeToCategory(node));
        this.applySort();
        this.loading = false;
        // Country builds a heatmap too when in heatmap view; categories still
        // load for the map view either way.
        if (this.selectedTab !== 'country' || this.view === 'heatmap')
          this.loadHeatmap();
      });
  }

  // Build the 12-month heatmap from `signal/market_board`: one call per calendar
  // month, each returning the full treemap (categories + their supersector
  // children) scored for that month. This is the same endpoint the board already
  // trusts for sentiment, so parents AND sub-categories get real values from one
  // source — no score_ts, no company aggregation.
  private loadHeatmap(): void {
    const tab = this.selectedTab;
    this.heatmapReference = new Date();
    this.heatmapMonths = lastNMonths(this.heatmapReference, 12);
    this.heatmapLoading = true;
    this.heatmap = null;
    this.childEntitiesByParent.clear();
    this.expandedRows.clear();
    this.subRowsByParent.clear();

    const requests = this.heatmapMonths.map((month) =>
      this.entityInfo
        .getHierarchyInfo(tab, {
          time: monthRange(month),
          period: this.heatmapPeriod,
        })
        .pipe(catchError(() => of<unknown>({}))),
    );

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe((treemaps) => {
        const monthMaps = treemaps.map((t) => treemapScores(t));
        // Entity structure (parents + their sub-categories) from the latest month.
        const { parents, childrenByParent } = treemapStructure(
          treemaps[treemaps.length - 1],
        );
        this.childEntitiesByParent = childrenByParent;

        const allKeys = [
          ...parents.map((p) => p.compoundKey),
          ...Array.from(childrenByParent.values())
            .flat()
            .map((c) => c.compoundKey),
        ];
        const monthlyByKey = monthlyByKeyFromMaps(allKeys, monthMaps);

        this.heatmap = buildHeatmapFromMonthly(
          parents,
          monthlyByKey,
          this.heatmapMonths,
        );
        // Sub-category rows come from the same treemaps, so precompute them —
        // expanding is then instant.
        childrenByParent.forEach((subs, parentKey) => {
          this.subRowsByParent.set(
            parentKey,
            buildHeatmapFromMonthly(subs, monthlyByKey, this.heatmapMonths)
              .rows,
          );
        });
        this.heatmapLoading = false;
      });
  }

  get visibleHeatmapRows(): HeatmapRow[] {
    const rows = this.heatmap?.rows ?? [];
    const q = this.searchTerm.trim().toLowerCase();
    return q ? rows.filter((r) => r.name.toLowerCase().includes(q)) : rows;
  }

  // Singular header for the heatmap's first column ("Country" / "Index" / "Industry").
  get heatmapEntityHeader(): string {
    if (this.selectedTab === 'country') return 'Country';
    return this.selectedTab === 'index' ? 'Index' : 'Industry';
  }

  // Summary-row label ("Avg. Industries" / "Avg. Indices").
  get heatmapAvgLabel(): string {
    return 'Avg. ' + this.tabLabel(this.selectedTab);
  }

  hasSubCategories(row: HeatmapRow): boolean {
    return this.childEntitiesByParent.has(row.compoundKey);
  }

  isRowExpanded(row: HeatmapRow): boolean {
    return this.expandedRows.has(row.compoundKey);
  }

  subRowsOf(row: HeatmapRow): HeatmapRow[] {
    return this.subRowsByParent.get(row.compoundKey) ?? [];
  }

  // Expand/collapse a category's sub-categories. Their rows are precomputed in
  // loadHeatmap (same treemaps), so this is a pure toggle — no fetch.
  toggleRow(row: HeatmapRow): void {
    const key = row.compoundKey;
    if (this.expandedRows.has(key)) {
      this.expandedRows.delete(key);
    } else {
      this.expandedRows.add(key);
    }
  }

  cellColor(cell: HeatmapCell): TierColor | null {
    return cell.pct === null ? null : heatmapCellColor(cell.pct);
  }

  cellArrow(cell: HeatmapCell): string {
    return deltaArrow(cell.delta);
  }

  cellLabel(cell: HeatmapCell): string {
    return cell.pct === null ? '–' : `${cell.pct}%`;
  }

  cellAria(name: string, month: HeatmapMonth, cell: HeatmapCell): string {
    return cellAriaLabel(name, month.label, cell);
  }

  // ── Heatmap cell → detail panel ─────────────────────────────────────────
  // A cell carries data only when it has a percentage; empty months open nothing.
  cellClickable(cell: HeatmapCell): boolean {
    return cell.pct !== null;
  }

  // Open the panel for one entity (row or sub-row) at the clicked month. The
  // panel loads that entity's constituents + key events over the month's range.
  openCellPanel(row: HeatmapRow, monthIndex: number): void {
    const month = this.heatmap?.months[monthIndex];
    const cell = row.cells[monthIndex];
    if (!month || !cell || cell.pct === null) return;

    this.panelTimeframe = {
      time: monthRange(month),
      period: this.heatmapPeriod,
    };
    this.panelTimeframeLabel = `${month.label} sentiment`;
    this.panelSentimentParams = {
      ...this.route.snapshot.queryParams,
      time: monthRange(month),
      period: this.heatmapPeriod,
    };
    this.panelAggregate = false;
    this.panelEntity = {
      id: idFromCompoundKey(row.compoundKey),
      type: this.selectedTab,
      compoundKey: row.compoundKey,
      name: row.name,
      constituentCount: 0,
      neg: 0,
      neu: 0,
      pos: 0,
      spread: 0,
      average: cell.pct,
      delta: cell.delta ?? 0,
      movers: { up: 0, down: 0 },
      topEvent: undefined,
      constituents: [],
      children: [],
      expanded: false,
      loading: false,
    };
  }

  // Open the panel for the average summary row at the clicked month. Its
  // "constituents" are the tab's entities, each scored for that month — no
  // fetch, so the panel renders in aggregate mode.
  openAvgPanel(monthIndex: number): void {
    const month = this.heatmap?.months[monthIndex];
    const avgCell = this.heatmap?.avg[monthIndex];
    if (!this.heatmap || !month || !avgCell || avgCell.pct === null) return;

    const avgPct = avgCell.pct;
    const constituents: Constituent[] = [];
    const children: { sentiment: number }[] = [];
    let neg = 0;
    let neu = 0;
    let pos = 0;

    this.heatmap.rows.forEach((r) => {
      const cell = r.cells[monthIndex];
      if (cell.pct === null) return;
      const bucket = sentimentBucket(cell.pct);
      if (bucket === 'pos') pos += 1;
      else if (bucket === 'neu') neu += 1;
      else neg += 1;
      children.push({ sentiment: cell.pct / 100 });
      constituents.push({
        compoundKey: r.compoundKey,
        name: r.name,
        score: cell.pct,
        bucket,
        volume: 0,
        impact: Math.round(cell.pct - avgPct),
        delta: cell.delta ?? 0,
        topEvent: undefined,
      });
    });
    constituents.sort((a, b) => a.score - b.score);

    this.panelTimeframe = DEFAULT_TIMEFRAME;
    this.panelTimeframeLabel = `${month.label} · ${this.tabLabel(
      this.selectedTab,
    ).toLowerCase()}`;
    this.panelSentimentParams = {};
    this.panelAggregate = true;
    this.panelEntity = {
      id: `${this.selectedTab}:avg`,
      type: this.selectedTab,
      compoundKey: undefined,
      name: this.heatmapAvgLabel,
      constituentCount: constituents.length,
      neg,
      neu,
      pos,
      spread: 0,
      average: avgPct,
      delta: avgCell.delta ?? 0,
      movers: { up: 0, down: 0 },
      topEvent: undefined,
      constituents,
      children,
      expanded: false,
      loading: false,
    };
  }

  closeCellPanel(): void {
    this.panelEntity = null;
  }

  trackByHeatmapRow(_i: number, row: HeatmapEntity): string {
    return row.compoundKey;
  }

  trackByMonth(_i: number, month: HeatmapMonth): string {
    return month.key;
  }

  private mapNodeToCategory(node: any): Category {
    const entity = node.entity ?? {};
    const grand = (node.children ?? []) as any[];

    let neg = node.neg ?? 0;
    let neu = node.neu ?? 0;
    let pos = node.pos ?? 0;
    let count = node.count ?? grand.length;

    if (!neg && !neu && !pos && grand.length > 0) {
      grand.forEach((c: any) => {
        const s = pickScore(c);
        if (!Number.isFinite(s)) return;
        const b = sentimentBucket(s * 100);
        if (b === 'pos') pos += 1;
        else if (b === 'neu') neu += 1;
        else neg += 1;
      });
      count = pos + neu + neg;
    }

    const scores = grand
      .map((c: any) => pickScore(c))
      .filter((v): v is number => Number.isFinite(v));

    const nodeScore = pickScore(node);
    const avg = Number.isFinite(nodeScore)
      ? nodeScore
      : scores.length > 0
        ? scores.reduce((a, b) => a + b, 0) / scores.length
        : 0;

    const minS = scores.length ? Math.min(...scores) : avg;
    const maxS = scores.length ? Math.max(...scores) : avg;
    const delta = pickDelta(node);
    const movers = this.pickMovers(node, grand);

    return {
      id: entity.alpha_id ?? entity.compound_key ?? entity.name,
      type: entity.type ?? '',
      compoundKey: entity.compound_key,
      name: entity.name ?? entity.compound_key ?? '',
      hcKey: entity.hc_key ?? entity['hc-key'] ?? undefined,
      constituentCount: count || grand.length,
      neg,
      neu,
      pos,
      spread: Math.round((maxS - minS) * 100),
      average: Math.round(avg * 100),
      delta: Number.isFinite(delta) ? Math.round(delta * 100) : 0,
      movers,
      topEvent: this.pickTopEvent(node),
      constituents: [],
      children: grand,
      expanded: false,
      loading: false,
    };
  }

  private loadConstituents(cat: Category): void {
    const nodes = (cat.children ?? []) as any[];
    const constituents: Constituent[] = nodes
      .map((node) => {
        const entity = node?.entity ?? {};
        const key = entity.compound_key;
        if (!key) return null;
        const score = pickScore(node);
        const delta = pickDelta(node);
        const volume = pickVolume(node);
        const bucket = sentimentBucket(score * 100);
        const impact = Math.round((score - cat.average / 100) * 100);
        return {
          compoundKey: key,
          name: entity.name ?? key,
          score: Math.round((score || 0) * 100),
          bucket,
          volume,
          impact,
          delta: Number.isFinite(delta) ? Math.round(delta * 100) : 0,
          topEvent: this.pickTopEvent(node),
        } as Constituent;
      })
      .filter((c): c is Constituent => !!c);

    cat.constituents = constituents
      .filter((c) => Number.isFinite(c.score))
      .sort((a, b) => a.score - b.score);
    cat.loading = false;
  }

  private pickMovers(node: any, grand: any[]): { up: number; down: number } {
    if (node?.movers && typeof node.movers === 'object') {
      return {
        up: Number(node.movers.up) || 0,
        down: Number(node.movers.down) || 0,
      };
    }
    let up = 0;
    let down = 0;
    grand.forEach((c: any) => {
      const d = pickDelta(c);
      if (!Number.isFinite(d)) return;
      if (d > 0) up += 1;
      else if (d < 0) down += 1;
    });
    return { up, down };
  }

  private pickTopEvent(raw: any): TopEvent | undefined {
    if (!raw) return undefined;
    const source =
      raw.top_event ??
      (Array.isArray(raw.top_events) ? raw.top_events[0] : undefined) ??
      (Array.isArray(raw.events) ? raw.events[0] : undefined);
    if (!source) return undefined;
    const label =
      source.label ??
      source.name ??
      source.event?.label ??
      source.event?.name ??
      source.event?.id;
    const volume = source.volume ?? source.count ?? source.news_volume ?? 0;
    if (!label) return undefined;
    return { label: String(label), volume: Number(volume) || 0 };
  }

  private applySort(): void {
    const sorted = this.categories.slice();
    if (this.sort === 'worst') {
      sorted.sort((a, b) => a.average - b.average);
    } else if (this.sort === 'best') {
      sorted.sort((a, b) => b.average - a.average);
    } else {
      sorted.sort((a, b) => b.spread - a.spread);
    }
    // New array reference so the overview child re-ranks on sort changes.
    this.categories = sorted;
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
